import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { BackgroundGeolocation } from "@capgo/background-geolocation";
import { environment } from "@env/environment";
import { firstValueFrom } from "rxjs";
import { scheduledToInstant, todayInTimezone } from "@fsm/shared";
import { TenantCapabilitiesService } from "./tenant-capabilities.service";
import { OfflineStorageService } from "../../offline/offline-storage.service";
import { TenantDateService } from "../../shared/services/tenant-date.service";
import {
  GpsQueueService,
  MAX_BATCH,
  type QueuedGpsPoint,
} from "./gps-queue.service";

/**
 * Adaptive-frequency tiers (ms between accepted points), by movement state
 * derived from GPS speed (spec gps-route-optimization Req 2.1 / 15.2).
 */
const INTERVAL_FAST_MS = 15_000; // speed > 5 m/s (driving)
const INTERVAL_MEDIUM_MS = 60_000; // 1–5 m/s (walking)
const INTERVAL_SLOW_MS = 120_000; // < 1 m/s sustained (stationary)
/** Degraded interval forced below BATTERY_DEGRADE_PCT (Req 2.5 / 15.4). */
const INTERVAL_DEGRADED_MS = 180_000;

/** Speed thresholds in m/s for tier selection. */
const SPEED_FAST_MS = 5;
const SPEED_SLOW_MS = 1;
/** Sustained-stationary window before dropping to the slow tier (Req 2.1). */
const STATIONARY_SUSTAIN_MS = 30_000;

/** Battery thresholds (percent): degrade frequency, then pause entirely. */
const BATTERY_DEGRADE_PCT = 15;
const BATTERY_PAUSE_PCT = 10;
/** How long a battery reading is reused before re-querying the device. */
const BATTERY_CACHE_MS = 60_000;

/**
 * GpsTrackingService — Continuous background GPS tracking for field technicians.
 *
 * Activated when a work order enters IN_PROGRESS. Uses
 * @capgo/background-geolocation to capture location, buffering each point to a
 * SQLite offline queue (GpsQueueService, Req 2.4 / 15.9) and flushing it to
 * POST /v1/gps/locations in batches of up to 50 (Req 2.3 / 15.6) in the
 * compressed array format the backend expects.
 *
 * Stops when the work order is completed or the technician stops tracking.
 *
 * BACKGROUND BEHAVIOR IS PLATFORM-SPECIFIC (App Store Review Guideline 2.5.4):
 *  - Android: collection continues while the app is minimized or the screen is
 *    locked (spec gps-route-optimization Req 2.6 / 15.1 / 15.8). The native
 *    plugin runs a foreground service, so backgrounding does NOT stop capture.
 *  - iOS: foreground-only. Apple rejects `UIBackgroundModes: location` when the
 *    only use is employee tracking, so the iOS build declares no location
 *    background mode and capture is suspended when the app leaves the
 *    foreground. beginWatcher() omits the background message on iOS, and
 *    suspend()/resumeIfTracking() stop and restart the native watcher around
 *    foreground/background transitions.
 */
@Injectable({ providedIn: "root" })
export class GpsTrackingService {
  private readonly http = inject(HttpClient);
  private readonly capabilities = inject(TenantCapabilitiesService);
  private readonly queue = inject(GpsQueueService);
  private readonly storage = inject(OfflineStorageService);
  private readonly tenantDate = inject(TenantDateService);
  private readonly apiUrl = environment.apiUrl;

  /** Whether a tracking session is currently active. */
  readonly isTracking = signal(false);

  /**
   * Set to true when the session was stopped automatically because the
   * configured end of the workday was reached (spec gps-route-optimization Req
   * 13.4). The UI observes this to show a toast; it is cleared on the next
   * start(). Consentimiento: this is the ONLY automatic transition — tracking
   * never auto-STARTS (start() is called solely from explicit user actions:
   * "Ya llegué" and the "Activar" banner).
   */
  readonly stoppedBySchedule = signal(false);

  /** Timer handle for the end-of-workday auto-stop. */
  private endOfDayTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * True when tracking is paused because the battery dropped below
   * BATTERY_PAUSE_PCT. The session stays active (isTracking() === true) but the
   * native watcher is stopped and the UI can offer a manual resume (Req 15.4).
   */
  readonly lowBatteryPaused = signal(false);

  /** True once BackgroundGeolocation.start() has been called for this session. */
  private watcherStarted = false;

  /** Guards against overlapping flush() runs. */
  private flushing = false;

  /**
   * OT activa de la sesión: el `workOrderId` de la orden que el técnico ejecuta.
   * Cada punto capturado se atribuye a ella (spec gps-route-per-order Req 1).
   * `undefined` cuando la sesión arranca sin OT (banner "Activar" general).
   */
  private activeWorkOrderId?: string;

  /**
   * Timestamp (ms) of the last point accepted into the queue. -Infinity means
   * "none yet", so the first point always passes the adaptive-frequency gate
   * regardless of the wall clock.
   */
  private lastAcceptedMs = Number.NEGATIVE_INFINITY;

  /** Timestamp (ms) when the device was first seen stationary (speed < slow). */
  private stationarySinceMs: number | null = null;

  /** Cached battery level (0–100) and when it was read, to avoid per-point calls. */
  private cachedBatteryPct = 100;
  /** -Infinity forces the first readBatteryPct() to actually query the device. */
  private batteryReadMs = Number.NEGATIVE_INFINITY;

  /**
   * Start GPS tracking. Call when a work order transitions to IN_PROGRESS.
   *
   * @param workOrderId OT que se ejecuta — atribuye el recorrido a esa orden
   *   (spec gps-route-per-order Req 1). Omitir para una sesión sin OT (banner
   *   "Activar" general).
   */
  start(workOrderId?: string): void {
    if (this.isTracking()) {
      // La sesión sigue viva entre OTs (Req 2.7): solo re-atribuimos los puntos
      // a la nueva OT y, si el watcher nativo no está corriendo (p.ej. se cayó),
      // lo reanudamos. Antes este return dejaba la OT nueva sin atribuir.
      if (workOrderId !== undefined) this.activeWorkOrderId = workOrderId;
      if (!this.watcherStarted && !this.lowBatteryPaused()) {
        void this.beginWatcher();
      }
      return;
    }
    this.activeWorkOrderId = workOrderId;

    // Module gating — gps_tracking is Business/add-on only. If the tenant's plan
    // does not include it, do NOT capture or send points: the backend would
    // reject POST /v1/gps/locations with 422 BUSINESS_MODULE_NOT_AVAILABLE.
    // The signal is normally populated by TenantCapabilitiesService.load()
    // before a work order reaches IN_PROGRESS; as a safety net we (re)load
    // capabilities if it is not yet true so a legitimate Business tenant is
    // never blocked by an unpopulated signal.
    if (this.capabilities.gpsTrackingAvailable()) {
      void this.beginTracking();
      return;
    }

    void this.capabilities.load().then(() => {
      if (this.capabilities.gpsTrackingAvailable()) {
        void this.beginTracking();
      } else {
        console.info(
          "[GpsTracking] gps_tracking module not enabled — not tracking",
        );
      }
    });
  }

  /** Begin a tracking session. Assumes module gating already passed. */
  private async beginTracking(): Promise<void> {
    if (this.isTracking()) return;
    this.isTracking.set(true);
    this.lowBatteryPaused.set(false);
    this.stoppedBySchedule.set(false);
    this.lastAcceptedMs = Number.NEGATIVE_INFINITY;
    this.stationarySinceMs = null;
    this.batteryReadMs = Number.NEGATIVE_INFINITY;
    await this.beginWatcher();
    void this.scheduleEndOfDayStop();
  }

  /**
   * Schedule an automatic stop at the end of the tenant's configured workday
   * (spec gps-route-optimization Req 13.4). Reads cached business hours, finds
   * today's closing time for the current weekday in the tenant timezone, and
   * if that instant is still in the future, arms a timer that stops tracking
   * and raises `stoppedBySchedule` so the UI can toast. No-op when the tenant
   * has no business hours (e.g. field tenants) — then only manual stop applies
   * (Req 13.5). Safe on web.
   */
  private async scheduleEndOfDayStop(): Promise<void> {
    this.clearEndOfDayTimer();
    try {
      const config = await this.storage.getVerticalConfig();
      const raw = config?.businessHoursJson;
      if (!raw) return;

      const business = JSON.parse(raw) as {
        timezone?: string;
        schedule?: { day: number; open: string; close: string }[];
      };
      const schedule = business.schedule;
      if (!schedule || schedule.length === 0) return;

      const tz = business.timezone || this.tenantDate.getTimezone();
      const today = todayInTimezone(tz); // YYYY-MM-DD in tenant tz
      const weekday = this.weekdayInTimezone(today, tz); // 0=Sun..6=Sat
      const todaySchedule = schedule.find((s) => s.day === weekday);
      if (!todaySchedule?.close) return;

      const closeInstant = scheduledToInstant(today, todaySchedule.close, tz);
      const msUntilClose = closeInstant.getTime() - Date.now();
      if (msUntilClose <= 0) return; // Already past closing — leave manual control.

      this.endOfDayTimer = setTimeout(() => {
        if (!this.isTracking()) return;
        this.stoppedBySchedule.set(true);
        this.stop();
      }, msUntilClose);
    } catch {
      // Corrupt/missing config — fail open: no auto-stop, manual stop still works.
    }
  }

  /** Weekday (0=Sunday..6=Saturday) for a YYYY-MM-DD civil date in a timezone. */
  private weekdayInTimezone(date: string, timezone: string): number {
    // Resolve the date at noon (avoids DST edges) to a real instant, then read
    // the weekday as rendered in the tenant timezone.
    const instant = scheduledToInstant(date, "12:00", timezone);
    const name = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      timeZone: timezone,
    }).format(instant);
    const map: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    return map[name] ?? 0;
  }

  /** Clear the end-of-workday auto-stop timer if armed. */
  private clearEndOfDayTimer(): void {
    if (this.endOfDayTimer) {
      clearTimeout(this.endOfDayTimer);
      this.endOfDayTimer = null;
    }
  }

  /**
   * Start (or restart) the native background watcher. Separated from
   * beginTracking() so a low-battery pause can restart capture via
   * resumeFromLowBattery() without resetting the whole session.
   */
  private async beginWatcher(): Promise<void> {
    // On web (dev server) the plugin has a limited fallback and there is no
    // real background mode; keep the session flagged active but skip the
    // native watcher so unit/dev runs do not error.
    if (Capacitor.getPlatform() === "web") return;

    // Wait for any in-flight stop (from the previous order) before starting.
    await this.watcherOp;
    if (this.watcherStarted) return;

    // iOS: foreground-only (App Store Review Guideline 2.5.4). Apple rejects
    // `UIBackgroundModes: location` when the only use is employee tracking, so
    // the iOS build must NOT collect location in the background. On iOS we omit
    // `backgroundTitle`/`backgroundMessage` — their presence is exactly what
    // makes @capgo/background-geolocation request "Always" and keep delivering
    // while the app is backgrounded/locked. Without them the plugin asks only
    // for "When In Use" and the OS suspends capture when the app leaves the
    // foreground (capture is restarted on resume by resumeIfTracking()).
    // Android is unaffected and keeps full background tracking via the
    // foreground service (see AndroidManifest FOREGROUND_SERVICE_LOCATION and
    // capacitor.config.ts useLegacyBridge).
    const isIos = Capacitor.getPlatform() === "ios";

    try {
      await BackgroundGeolocation.start(
        {
          // Presence of backgroundMessage enables background delivery and shows
          // the persistent Android notification (Req 13.1 "Ubicación activa").
          // Omitted on iOS to keep capture foreground-only (Guideline 2.5.4).
          ...(isIos
            ? {}
            : {
                backgroundTitle: "Ubicación activa",
                backgroundMessage: "SIF agent está registrando tu ubicación.",
              }),
          requestPermissions: true,
          stale: false,
          // Capture on any movement; the backend TTL + dedup handle volume.
          // Fine-grained cadence is enforced in onLocation() by the adaptive
          // interval + battery policy.
          distanceFilter: 10,
        },
        (location, error) => {
          if (error) {
            console.warn("[GpsTracking] watcher error:", error);
            return;
          }
          if (location) {
            void this.onLocation(location);
          }
        },
      );
      this.watcherStarted = true;
      console.info("[GpsTracking] background watcher started", {
        workOrderId: this.activeWorkOrderId,
      });
    } catch (err) {
      console.warn("[GpsTracking] Failed to start background watcher:", err);
      this.isTracking.set(false);
    }
  }

  /**
   * Handle one captured point: filter unreliable fixes, apply battery policy
   * (degrade / pause) and adaptive-frequency throttling, then enqueue to SQLite
   * and flush opportunistically when a full batch has accumulated.
   */
  private async onLocation(location: {
    latitude: number;
    longitude: number;
    accuracy: number;
    speed?: number | null;
    bearing?: number | null;
    time?: number | null;
  }): Promise<void> {
    const now = Date.now();

    // Discard unreliable fixes (Req 15.7: drop accuracy worse than 100m).
    if (location.accuracy != null && location.accuracy > 100) return;

    // Battery policy (Req 2.5 / 15.4). Below the pause threshold, stop the
    // native watcher entirely and surface the state so the UI can offer resume.
    const battery = await this.readBatteryPct();
    if (battery <= BATTERY_PAUSE_PCT) {
      this.lowBatteryPaused.set(true);
      void this.stopWatcher();
      return;
    }

    // Adaptive-frequency gate: accept a point only once the current tier's
    // interval has elapsed since the last accepted one (Req 2.1 / 15.2). Below
    // the degrade threshold the interval is forced to INTERVAL_DEGRADED_MS.
    const interval =
      battery <= BATTERY_DEGRADE_PCT
        ? INTERVAL_DEGRADED_MS
        : this.selectIntervalMs(location.speed ?? null, now);
    if (now - this.lastAcceptedMs < interval) {
      return;
    }
    this.lastAcceptedMs = now;

    const point: QueuedGpsPoint = {
      localId: this.makeId(),
      lat: location.latitude,
      lng: location.longitude,
      ts: new Date(location.time ?? now).toISOString(),
      speed: location.speed ?? null,
      heading: location.bearing ?? null,
      accuracy: location.accuracy ?? 0,
      battery: Math.round(battery),
      ...(this.activeWorkOrderId !== undefined && {
        workOrderId: this.activeWorkOrderId,
      }),
    };

    try {
      await this.queue.enqueue(point);
    } catch (err) {
      console.warn("[GpsTracking] enqueue failed:", err);
      return;
    }

    const pending = await this.queue.pendingCount();
    if (pending >= MAX_BATCH) {
      void this.flush(false);
    }
  }

  /**
   * Select the adaptive-frequency interval from the movement state derived from
   * GPS speed (Req 2.1 / 15.2):
   *   - > 5 m/s           → 15 s (driving/riding)
   *   - 1–5 m/s           → 60 s (walking/slow)
   *   - < 1 m/s sustained ≥ 30 s → 120 s (stationary)
   * Until the stationary window is sustained, the medium interval applies so a
   * brief stop does not immediately drop to the slow tier.
   */
  private selectIntervalMs(speed: number | null, now: number): number {
    const s = speed ?? 0;
    if (s > SPEED_FAST_MS) {
      this.stationarySinceMs = null;
      return INTERVAL_FAST_MS;
    }
    if (s >= SPEED_SLOW_MS) {
      this.stationarySinceMs = null;
      return INTERVAL_MEDIUM_MS;
    }
    // Stationary (speed < 1 m/s). Require it to be sustained before slowing.
    if (this.stationarySinceMs == null) {
      this.stationarySinceMs = now;
      return INTERVAL_MEDIUM_MS;
    }
    return now - this.stationarySinceMs >= STATIONARY_SUSTAIN_MS
      ? INTERVAL_SLOW_MS
      : INTERVAL_MEDIUM_MS;
  }

  /**
   * Current battery level as a percentage (0–100), cached for BATTERY_CACHE_MS
   * so it is not queried on every location callback. Falls back to the last
   * known value (or 100) if the Device plugin is unavailable (e.g. web).
   */
  private async readBatteryPct(): Promise<number> {
    const now = Date.now();
    if (now - this.batteryReadMs < BATTERY_CACHE_MS) {
      return this.cachedBatteryPct;
    }
    try {
      const info = await Device.getBatteryInfo();
      // batteryLevel is 0–1; convert to percent. Undefined on some platforms.
      // Some devices report -1 when the level is unknown; ignore it, otherwise
      // the point fails backend validation (battery 0–100) and the session
      // would be paused as "low battery".
      if (
        typeof info.batteryLevel === "number" &&
        info.batteryLevel >= 0 &&
        info.batteryLevel <= 1
      ) {
        this.cachedBatteryPct = info.batteryLevel * 100;
      }
    } catch {
      // Device plugin unavailable (web/dev) — keep the last known value.
    }
    this.batteryReadMs = now;
    return this.cachedBatteryPct;
  }

  /**
   * Manually resume tracking after a low-battery pause (Req 15.5). Re-evaluates
   * state on resume: clears the paused flag, resets the movement window, and
   * restarts the native watcher. No-op if not paused or not tracking.
   */
  resumeFromLowBattery(): void {
    if (!this.isTracking() || !this.lowBatteryPaused()) return;
    this.lowBatteryPaused.set(false);
    this.stationarySinceMs = null;
    this.lastAcceptedMs = Number.NEGATIVE_INFINITY;
    this.batteryReadMs = Number.NEGATIVE_INFINITY;
    void this.beginWatcher();
  }

  /**
   * Suspend — called by AppLifecycleService when the app leaves the foreground.
   *
   * Platform-split for App Store Review Guideline 2.5.4:
   *  - iOS: foreground-only. The iOS build declares no `location` background
   *    mode, so the OS would suspend capture anyway; we stop the native watcher
   *    explicitly to release GPS and avoid any background-location behavior.
   *    Capture is restarted on foreground by resumeIfTracking().
   *  - Android: background tracking must continue with the screen off (Req
   *    15.8). The foreground service keeps running, so this is a no-op for
   *    capture.
   * No-op if not tracking.
   */
  suspend(): void {
    if (!this.isTracking()) return;
    if (Capacitor.getPlatform() === "ios") {
      // iOS foreground-only: release the watcher while backgrounded.
      void this.stopWatcher();
    }
    // Android: intentionally does not stop the watcher. See class doc.
  }

  /**
   * Resume — called when the app returns to foreground.
   *
   * Platform-split (see suspend()):
   *  - iOS: capture was stopped on suspend(), so restart the native watcher
   *    (unless paused for low battery). The opportunistic flush still runs.
   *  - Android: capture never stopped; only trigger an opportunistic flush of
   *    whatever accumulated while the WebView was suspended.
   * No-op if not tracking.
   */
  resumeIfTracking(): void {
    if (!this.isTracking()) return;
    if (
      Capacitor.getPlatform() === "ios" &&
      !this.watcherStarted &&
      !this.lowBatteryPaused()
    ) {
      // iOS foreground-only: re-arm capture that suspend() released.
      void this.beginWatcher();
    }
    void this.flush(false);
  }

  /**
   * Stop GPS tracking: stop the native watcher, flush the remaining queue, and
   * notify the backend the session ended. Call when the work order is completed
   * or the technician navigates away.
   */
  stop(): void {
    if (!this.isTracking()) return;
    this.isTracking.set(false);
    this.lowBatteryPaused.set(false);
    this.activeWorkOrderId = undefined;
    this.clearEndOfDayTimer();

    void this.stopWatcher();
    // Flush remaining points + send sessionEnded.
    void this.flush(true);
  }

  /**
   * Called when a work order is completed. Ends the attribution to that order
   * (gps-route-per-order Req 1.4: never attribute points to a completed OT) and
   * only ends the whole Tracking_Session when the technician has no other
   * ASSIGNED/IN_PROGRESS order for today (gps-route-optimization Req 2.7).
   * Previously every completion called stop(), so tracking died after the
   * first order of the day.
   */
  async onWorkOrderCompleted(workOrderId: string): Promise<void> {
    if (!this.isTracking()) return;
    if (this.activeWorkOrderId === workOrderId) {
      this.activeWorkOrderId = undefined;
    }

    let remainingToday = 0;
    try {
      const today = todayInTimezone(this.tenantDate.getTimezone());
      const pending = await this.storage.getWorkOrders({
        status: ["ASSIGNED", "IN_PROGRESS"],
      });
      remainingToday = pending.filter(
        (wo) =>
          wo.workOrderId !== workOrderId &&
          (wo.scheduledDate ?? "").slice(0, 10) <= today,
      ).length;
    } catch {
      // SQLite unavailable (web) → keep previous behavior: end the session.
    }

    if (remainingToday === 0) {
      this.stop();
    } else {
      void this.flush(false);
    }
  }

  /**
   * Serializes native watcher start/stop so a stop still in flight from the
   * previous order can never kill the watcher started for the next one.
   */
  private watcherOp: Promise<void> = Promise.resolve();

  /** Stop the native background watcher if it was started. */
  private stopWatcher(): Promise<void> {
    this.watcherOp = this.watcherOp.then(async () => {
      if (!this.watcherStarted) return;
      this.watcherStarted = false;
      try {
        await BackgroundGeolocation.stop();
      } catch (err) {
        console.warn("[GpsTracking] Failed to stop watcher:", err);
      }
    });
    return this.watcherOp;
  }

  /**
   * Drain the offline queue to POST /v1/gps/locations in batches of up to 50,
   * using the compressed array format `[lat, lng, ts, speed, heading, accuracy,
   * battery]`. Successfully sent points are removed from the queue. If a send
   * fails (offline), points stay queued for the next attempt — no data loss.
   *
   * @param sessionEnded when true, send a final request with `sessionEnded`
   *   even if there are no points left (Req 3.9).
   */
  private async flush(sessionEnded: boolean): Promise<void> {
    if (this.flushing) return;
    this.flushing = true;
    try {
      // Drain as many full/partial batches as are queued.
      for (;;) {
        const batch = await this.queue.getUnsyncedBatch(MAX_BATCH);
        if (batch.length === 0) break;

        const points = batch.map((p) => {
          // Compressed tuple: [lat, lng, ts, speed, heading, accuracy, battery].
          // Append the workOrderId as the 8th position only when present, so the
          // backend attributes the point to that order (spec gps-route-per-order).
          const tuple: Array<number | string> = [
            p.lat,
            p.lng,
            p.ts,
            p.speed ?? 0,
            p.heading ?? 0,
            p.accuracy,
            p.battery,
          ];
          if (p.workOrderId) tuple.push(p.workOrderId);
          return tuple;
        });

        const sent = await this.sendBatch(points, false);
        if (!sent) {
          // Offline or server error — keep points queued and stop draining.
          return;
        }
        await this.queue.markSynced(batch.map((p) => p.localId));

        if (batch.length < MAX_BATCH) break;
      }

      if (sessionEnded) {
        await this.sendBatch([], true);
      }
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Send one batch to the backend. Returns true on success, false on
   * offline/error (so the caller can keep the points queued).
   */
  private async sendBatch(
    points: unknown[],
    sessionEnded: boolean,
  ): Promise<boolean> {
    const body: { points: unknown[]; sessionEnded?: boolean } = { points };
    if (sessionEnded) body.sessionEnded = true;

    try {
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/v1/gps/locations`, body, {
          // Telemetría en segundo plano: el técnico no puede corregir nada, así
          // que un fallo nunca debe mostrarse como toast ("Los datos ingresados
          // no son válidos...").
          headers: { "X-Suppress-Toast": "true" },
        }),
      );
      return true;
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 400 || status === 422) {
        // Rechazo de validación determinista: reintentar el mismo lote fallaría
        // siempre y bloquearía la cola FIFO. Se descarta (el caller lo marca
        // como sincronizado) y se registra para diagnóstico.
        console.warn(
          "[GpsTracking] Batch rejected by validation, discarding",
          status,
          (err as { error?: unknown })?.error,
        );
        return true;
      }
      // Offline or server error — points remain in the queue for retry.
      console.warn("[GpsTracking] Failed to send batch (offline or error)");
      return false;
    }
  }

  /** Generate a unique local id for a queued point. */
  private makeId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
